// Import external dependencies
import Swiper, { Navigation } from 'swiper';
import axios from 'axios';

// Import internal dependencies
import { getCookie, setCookie, removeCookie } from '../lib/utils.js';
import Alert from './alert.js';

class MasteriesSlider {
	constructor(el) {
		this.el = el;
		this.endpoint = window['mastery_frontend_data'].endpoint;
		this.cookieKey = 'mastery_submission';
		this.masteryOpen = false;
		this.toggleMasteryState();
		this.navNext = document.querySelector('#step-next');
		this.navPrev = document.querySelector('#step-prev');
		this.navFinish = document.querySelector('#step-finish');
		this.stepCurrent = document.querySelector('#step-current');
		this.alertEl = document.querySelector('#masteries-alert');
	}
	
	/**
	* Init the swiper slider.
	*/
	init() {
		Swiper.use([Navigation]);
		
		this.swiper = new Swiper(this.el, {
			slidesPerView: 1,
			spaceBetween: 20,
			loop: false,
			allowTouchMove: false,
			navigation: {
				nextEl: this.navNext,
				prevEl: this.navPrev,
			},
		});
		
		// Get existing submission data
		this.submissionData = this.getSubmissionData();
		this.handleExistingSubmissionData(); // Set existing submission data to inputs
		
		// If a input is modified, set data-modified to true
		this.handleRangeState();
		
		this.swiper.on('slideNextTransitionStart', () => {
			const fromSlide = this.swiper.activeIndex - 1;
			
			// Prevent going to next slide if no input is modified
			if (this.getRangeState(fromSlide) === false) {
				this.swiper.slideTo(fromSlide, 0);
				// Check if the device can vibrate, and vibrate if it can
				if (window.navigator.vibrate) {
					window.navigator.vibrate([50, 50]);
				}
				// Show alert message
				this.alert = new Alert(this.alertEl);
				this.alert.show();
				// Shake slide
				this.swiper.slides[fromSlide].classList.add(
					'mastery-card--shake'
					);
					// Set the state to true after first check, so user can submit default value if they wish.
					this.setRangeState(fromSlide, true);
					return; // Prevent further data proccessing if no input is modified
				}
				
				// Update submission data
				const scoreData = this.getMasteryScore(fromSlide);
				this.updateSubmissionData(scoreData);
			});
			
			// Update the step count every time the slide changes
			this.swiper.on('slideChange', () => {
				this.alert?.hide();
			
				const current = this.swiper.activeIndex + 1;
				this.updateNavBar(current);
				
				// Close the Mastery Summary if it's open and the slide changes.
				if (this.masteryOpen) {
					this.closeMasterySummary();
				}
			});
			
			// On click navFinish, submit data to server and open summary
			this.navFinish.addEventListener('click', e => {
				e.preventDefault();
				this.submitSubmissionData();  
				if (this.masteryOpen) {
					this.closeMasterySummary();
				} else {
					this.openMasterySummary();
				}
			});
			
			this.handleDraggingInContainer();
			
			// "Easteregg" for resetting the submission data by double-clicking the navbar steps
			document
			.querySelector('.navbar__steps')
			.addEventListener('dblclick', () => {
				console.log('Resetting submission data');
				this.resetSubmissionData();
			});
		}
		
		handleDraggingInContainer() {
			const inputs = document.querySelectorAll('input[name="mastery-score"]');
			inputs.forEach(input => {
				input.parentElement.addEventListener('touchstart', () => {
					input.classList.add('mastery-card__range--dragging');
				});
				input.parentElement.addEventListener('touchmove', e => {
					e.stopPropagation();
					const val = (e.pageX / window.innerWidth) * 100;
					input.value = Math.round(val);
				});
				input.parentElement.addEventListener('touchend', () => {
					input.classList.remove('mastery-card__range--dragging');
				});
			});
		}
		
		toggleMasteryState() {
			document.addEventListener("toggleslideover", () => {
				if (this.masteryOpen) {
					this.closeMasterySummary();
				} else {
					this.openMasterySummary();
				}
			})
		}

		/**
		* Insert content and open the Mastery Summary
		*/
		openMasterySummary() {
			this.masteryOpen = true;
			this.handleMasterySummaryContent();
			const open = new Event('slide-over-open');
			document.dispatchEvent(open);
		}
		
		/**
		* Close the Mastery Summary.
		*/
		closeMasterySummary() {
			this.masteryOpen = false;
			const close = new Event('slide-over-close');
			document.dispatchEvent(close);
		}
		
		/**
		* Generate Mastery summary content from the submission data
		*/
		handleMasterySummaryContent() {
			const score = this.calculateSubmissionData();
			const themeUrl = window['themeData'].theme_url;
			
			// Dynamic content elements
			const image = document.querySelector('#mastery-summary-image');
			const title = document.querySelector('#mastery-summary-title');
			const text = document.querySelector('#mastery-summary-text');
			
			let zone = null;
			let zoneText = null;
			
			if (score < 33.33) {
				zone = 'komfortsonen';
				zoneText = 'Det er behagelig å være i komfortsonen. Kanskje er du en trygg person, som sjelden blir urolig? Da skal du være glad og fornøyd med det! Hvis du føler deg ensom eller utenfor anbefaler vi at du prøver deg litt i utviklingssonen, selv om det er ubehagelig. Du har mye å vinne!'
			} else if (score < 66.66) {
				zone = 'utviklingssonen';
				zoneText = 'Heia deg! Det er bra at du pusher deg litt iblant. Hvis du føler deg ensom eller utenfor, håper vi du lykkes når du prøver nye måter å bli kjent med folk. Pass på at du også finner dine trygge steder, så du får slappe av litt i komfortsonen innimellom.'
			} else {
				zone = 'panikksonen';
				zoneText = 'Det kan se ut som du bruker mye krefter på å pushe deg selv. Kanskje litt for langt? Pass på at du også finner dine trygge steder, så du får slappe av litt i komfortsonen. Målene du setter deg skal være oppnåelige. Og helst skal du slippe å få panikk. Kan du finne et mål som pusher deg bare litt – og ikke helt ut i panikksonen?'
			}
			
			image.src = `${themeUrl}/dist/images/mastery-summary-${zone}.svg`;
			image.alt = zone;
			title.innerHTML = `Du er ofte i<br>${zone}`;
			text.innerHTML = zoneText;
		}
		
		/**
		* Update the navbar with the current step count.
		* Show the finish button if the last slide is reached.
		*
		* @param {number} count Current step count
		*/
		updateNavBar(count) {
			this.stepCurrent.innerHTML = count;
			
			// Disable next button if last slide and show finish button.
			if (count === this.swiper.slides.length) {
				this.navFinish.style.display = 'block';
				this.navNext.style.display = 'none';
			} else {
				this.navFinish.style.display = 'none';
				this.navNext.style.display = 'block';
			}
		}
		
		/**
		* Read the current submission data and go the the first slide that was not finished.
		* If all slides are finished, go to the last slide and open the Mastery Summary.
		*/
		handleExistingSubmissionData() {
			this.submissionData.data.map(obj => {
				const el = document.querySelector(
					`input[id="mastery-${obj.mastery_id}"]`
					);
					el.setAttribute('data-modified', 'true');
					el.value = obj.score;
				});
				
				let slideTo = this.swiper.slides
				.filter(s => this.getRange(s))
				.findIndex(
					s => this.getRange(s).getAttribute('data-modified') === 'false'
					);
					
					slideTo = slideTo === -1 ? this.swiper.slides.length - 1 : slideTo;
					
					this.swiper.slideTo(slideTo, 0);
					this.updateNavBar(slideTo + 1);
					
					// If the last slide is reached, open the Mastery Summary
					if (slideTo === this.swiper.slides.length - 1) {
						setTimeout(() => this.openMasterySummary(), 500); // Add a delay because it looks a bit more natural.
					}
				}
				
				/**
				* Add event listeners to the range inputs and set the data-modified attribute to true if the input is modified.
				*/
				handleRangeState() {
					const inputs = document.querySelectorAll('input[name="mastery-score"]');
					inputs.forEach(input => {
						input.addEventListener('input', () => {
							input.setAttribute('data-modified', true);
						});
					});
				}
				
				/**
				* Get range input in a specific slide.
				*
				* @param {HTMLElement || Int} elOrIndex
				* @returns {HTMLElement}
				*/
				getRange(elOrIndex) {
					let parent = null;
					if (elOrIndex instanceof HTMLElement) {
						parent = elOrIndex;
					} else if (typeof elOrIndex === 'number') {
						parent = this.swiper.slides[elOrIndex];
					}
					return parent.querySelector(`input[name="mastery-score"]`);
				}
				
				/**
				* Get the mastery score from a specific slide.
				*
				* @param {Int} index Slide index
				* @returns {Object} {mastery_id, score}
				*/
				getMasteryScore(index) {
					const el = this.getRange(index);
					return {
						score: el.value,
						mastery_id: el.id.split('-')[1],
					};
				}
				
				/**
				* Check if a range input has been modified by the data-modified attribute.
				*
				* @param {Int} index Slide index
				* @returns {Boolean}
				*/
				getRangeState(index) {
					const el = this.getRange(index);
					const val = el.getAttribute('data-modified');
					return val === 'true' ? true : false;
				}
				
				/**
				* Set the range input modified state
				*
				* @param {Int} index Slide index
				*/
				setRangeState(index, state = true) {
					const el = this.getRange(index);
					el.setAttribute('data-modified', state);
				}
				
				/**
				* Get submission data from cookie.
				*
				* @returns {Object}
				*/
				getSubmissionData() {
					let data = getCookie(this.cookieKey);
					return JSON.parse(atob(data));
				}
				
				/**
				* Set submission data to cookie.
				*
				* @param {Object} data
				*/
				updateSubmissionData(data) {
					const index = this.submissionData.data.findIndex(
						obj => obj.mastery_id === data.mastery_id
						);
						if (index > -1) {
							this.submissionData.data[index] = data;
						} else {
							this.submissionData.data.push(data);
						}
						
						const newData = btoa(JSON.stringify(this.submissionData));
						setCookie(this.cookieKey, newData);
					}
					
					/**
					* Submit the submission data to the server.
					*/
					submitSubmissionData() {
						const data = this.submissionData;
						axios.post(this.endpoint, data).catch(error => {
							console.error(error);
						});
					}
					
					/**
					* Reset the submission data by removing the cookie and refresing the page.
					*/
					resetSubmissionData() {
						removeCookie(this.cookieKey);
						window.location.reload();
					}
					
					/**
					* Calcualte the mastery score from the submission data.
					*
					* @returns {Int} 0-100
					*/
					calculateSubmissionData() {
						const data = this.submissionData;
						const total = data.data.length;
						const score = data.data.reduce((acc, obj) => {
							return acc + parseInt(obj.score);
						}, 0);
						return Math.round(score / total);
					}
				}
				
				export default MasteriesSlider;

				